import { useCallback, useMemo, useState } from 'react';

const useAttachFiles = ({
    onFilesAttached = () => {},
    onFilesRejected = () => {},
    canAttachFile = () => true,
    acceptedFormats = '',
}: {
    onFilesAttached?: () => void,
    onFilesRejected?: (files: File[]) => void,
    canAttachFile?: (file: File, acceptedFormats: string) => boolean,
    acceptedFormats?: string,
} = {}) => {
    const [attachedFiles, setAttachedFiles] = useState<File[]>([]);

    const attach = useCallback(
        (filesList: FileList | null) => {
            const files = filesList ?? [];
            if (files.length > 0) {
                const filesArray = Array.from(files as FileList);
                const validFiles = filesArray.filter(
                    (file) => canAttachFile(file, acceptedFormats),
                );
                const rejectedFiles = filesArray.filter(
                    (file) => !canAttachFile(file, acceptedFormats),
                );

                if (validFiles.length > 0) {
                    setAttachedFiles([
                        ...attachedFiles,
                        ...validFiles,
                    ]);
                    onFilesAttached();
                }

                if (rejectedFiles.length > 0) {
                    onFilesRejected(rejectedFiles);
                }
            }
        },
        [attachedFiles, onFilesAttached, onFilesRejected, canAttachFile, acceptedFormats],
    );

    const remove = useCallback(
        (index: number) => setAttachedFiles([
            ...attachedFiles.slice(0, index),
            ...attachedFiles.slice(index + 1),
        ]),
        [attachedFiles],
    );

    const setFiles = useCallback((files: File[]) => {
        setAttachedFiles(files);
    }, []);

    return useMemo(
        () => ({
            files: attachedFiles,
            attach,
            remove,
            setFiles,
            clear: () => setAttachedFiles([]),
        }),
        [attach, remove, setFiles, attachedFiles],
    );
};

export default useAttachFiles;
